import { na1 } from 'hubspot-url-utils/hublets';
import enviro from 'enviro';
import { useCallback, useMemo, useRef } from 'react';
import HubSpotVideoPlayer from 'ui-addon-video-player/components/HubSpotVideoPlayer';
import throttle from 'hs-lodash/throttle';
import { convertToMinutes } from '../../utils/time';
import { jsx as _jsx } from "react/jsx-runtime";
const ON_TIME_PROGRESS_THROTTLE_INTERVAL = 3000;
const ACVideoPlayer = ({
  videoId,
  autoPlay,
  startTime,
  mutedByDefault,
  onError = () => {},
  onFinish = () => {},
  onLoaded = () => {},
  onPause = () => {},
  onPlay = () => {},
  onReady = () => {},
  onProgress = () => {},
  onTimeUpdate: onTimeUpdateProp = () => {},
  onPlayBackRateChange = () => {},
  onTimeProgress = () => {},
  onActivation = () => {},
  playbackRate = 1,
  creatorHubId,
  creatorRegion = na1,
  activationThreshold = 10,
  subtitleLanguage,
  enablePictureInPicture = true,
  playerType,
  onToggleAutoPlay = () => {},
  enableAutoPlayToggle = false
}) => {
  const previousTime = useRef(startTime || 0);
  const throttledTimeUpdateMinutesWatched = useMemo(() => throttle(seconds => {
    const timeDifference = seconds - previousTime.current;
    previousTime.current = seconds;
    if (timeDifference > 0 &&
    // To exclude view duration events triggered by user-initiated time scrubbing via the video progress bar
    timeDifference < ON_TIME_PROGRESS_THROTTLE_INTERVAL * 2) {
      const viewDuration = convertToMinutes(timeDifference);
      onTimeProgress(viewDuration);
    }
  }, ON_TIME_PROGRESS_THROTTLE_INTERVAL, {
    leading: false,
    trailing: true
  }), [onTimeProgress]);
  const activationEventFired = useRef(false);
  const timeWatched = useRef(0);
  const granularPreviousTime = useRef(startTime || 0);
  const handleTimeUpdate = useCallback(seconds => {
    onTimeUpdateProp(seconds);
    const timeDelta = seconds - granularPreviousTime.current;
    if (timeDelta > 0 &&
    // prevents updating if the user skips backwards
    timeDelta < 1 // prevents updating if the users skips too far forwards
    ) {
      timeWatched.current += seconds - granularPreviousTime.current;
    }
    granularPreviousTime.current = seconds;
    throttledTimeUpdateMinutesWatched(seconds);
    if (timeWatched.current > activationThreshold && !activationEventFired.current) {
      onActivation();
      activationEventFired.current = true;
    }
  }, [onTimeUpdateProp, throttledTimeUpdateMinutesWatched, activationThreshold, onActivation]);
  const handlePlay = useCallback(() => {
    onPlay();
  }, [onPlay]);
  const handleProgressHsVideo = useCallback(e => {
    if (e && e.detail) {
      onProgress({
        currentTime: e.detail.currentTime,
        subtitleLanguage: e.detail.textLanguage
      });
    }
  }, [onProgress]);
  const handleTimeUpdateHsVideo = useCallback(e => {
    if (e && e.detail && e.detail.currentTime) {
      handleTimeUpdate(e.detail.currentTime);
    }
  }, [handleTimeUpdate]);
  const handleFinish = useCallback(() => {
    onFinish();
    if (!activationEventFired.current) {
      onActivation();
      activationEventFired.current = true;
    }
  }, [onActivation, onFinish]);
  const handleLoaded = useCallback(() => {
    onLoaded();
  }, [onLoaded]);
  return /*#__PURE__*/_jsx("div", {
    "data-player-type": "HUBSPOT_VIDEO",
    "data-player-video-id": videoId,
    "data-test-id": "ac-video-player",
    children: /*#__PURE__*/_jsx(HubSpotVideoPlayer, {
      autoPlay: autoPlay,
      currentTime: startTime !== undefined && startTime >= 0 ? startTime : undefined,
      enableAutoPlayToggle: enableAutoPlayToggle,
      enableParentHotKeys: true,
      enablePictureInPicture: enablePictureInPicture,
      enableVideoFrameHotKeys: true,
      env: enviro.isQa() ? 'qa' : 'prod',
      mutedByDefault: mutedByDefault,
      onError: onError,
      onFinish: handleFinish,
      onLoaded: handleLoaded,
      onPause: onPause,
      onPlay: handlePlay,
      onPlayBackRateChange: onPlayBackRateChange,
      onReady: onReady,
      onTimeUpdate: handleTimeUpdateHsVideo,
      onToggleAutoPlay: onToggleAutoPlay,
      onUpdateProgress: handleProgressHsVideo,
      playbackRate: playbackRate,
      playerType: playerType,
      portalId: creatorHubId,
      preload: "auto",
      region: creatorRegion,
      subtitleLanguage: subtitleLanguage,
      videoId: videoId,
      videoPlaySpeed: playbackRate * 100
    })
  });
};
export default ACVideoPlayer;